import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from '@/lib/api';

interface AuthState {
  authenticated: boolean;
  loading: boolean;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api
      .session()
      .then((s) => active && setAuthenticated(s.authenticated))
      .catch(() => active && setAuthenticated(false))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const value: AuthState = {
    authenticated,
    loading,
    async login(password: string) {
      await api.login(password);
      setAuthenticated(true);
    },
    async logout() {
      await api.logout();
      setAuthenticated(false);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
